import { useEffect, useState } from "react";
import {
  Alert, AlertIcon, Box, Button, Checkbox, CloseButton, Container, Flex, Heading, HStack,
  Icon, IconButton, Image, Input, InputGroup, InputRightElement, Link,
  LinkBox, LinkOverlay, Menu, MenuButton, MenuItem, MenuList, Modal, ModalBody,
  ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
  SimpleGrid, Text, useDisclosure, VStack,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiGlobe, FiUsers, FiTrendingUp, FiHelpCircle, FiRepeat, FiCoffee, FiDatabase,
  FiUploadCloud, FiAward, FiGrid, FiMap, FiType, FiCpu, FiZap, FiClipboard,
  FiCalendar, FiBookOpen, FiFileText, FiChevronDown, FiEye, FiEyeOff, FiPlayCircle,
} from "react-icons/fi";
import { API_BASE, DEFAULT_HEADERS } from "./services/apiClient";
import { getLocalizedField } from "./utils/newsLocalization";
import { getJournalLogoPath } from "./utils/journalLogos";
import { performLogin, requestPasswordReset, resendVerification } from "./services/authFlow";
import { LANGUAGE_OPTIONS } from "./constants/languages";
import SOCIAL_LINKS from "./constants/social";
import StoryDeck from "./components/StoryDeck";
import { ACCENT, ACCENT_HOVER, SERIF, PAPER } from "./theme/tokens";

const VIDEOS = ["/wine1.mp4", "/wine2.mp4", "/wine3.mp4", "/wine4.mp4", "/wine5.mp4"];
// One 720p clip per visit, looped. Playing all five in sequence cost ~230 MB per visit.
const POSTERS = VIDEOS.map((v) => v.replace(".mp4", "_poster.webp"));

// 15 feature mini-cards, reusing the multilingual login_slides keys
const FEATURE_KEYS = [
  [FiGlobe, "global_reach"], [FiUsers, "collaboration"], [FiTrendingUp, "insights"],
  [FiHelpCircle, "ask_expert"], [FiRepeat, "reverse_seminars"], [FiCoffee, "weekly_digest"],
  [FiDatabase, "winery_data"], [FiUploadCloud, "share_research"], [FiAward, "scholarly_profiles"],
  [FiGrid, "themes"], [FiMap, "regional_clusters"], [FiType, "multilingual"],
  [FiCpu, "smart_curation"], [FiZap, "actionable_insights"], [FiClipboard, "collaboration_board"],
];

const stripMd = (s = "") => s.replace(/[*#_>`]/g, "").replace(/^\s*-\s*/gm, "• ").trim();
const fmtDate = (d) => { const dt = new Date(d); return isNaN(dt) ? "" : dt.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); };

// Free preview of the country portrait: a handful of countries; the full set
// (and the deeper tools) is behind a free account.
const PORTRAIT_COUNTRIES = ["France", "Italy", "Spain", "USA", "Switzerland"];
const PORTRAIT_METRICS = [
  { key: "n_firms", label: "Surveyed firms", fmt: (v) => Number(v).toLocaleString() },
  { key: "pct_exporting", label: "Firms exporting", fmt: (v) => `${v}%` },
  { key: "pct_organic", label: "Organic firms", fmt: (v) => `${v}%` },
  { key: "avg_warming_c", label: "Warming since 1980s", fmt: (v) => `${v > 0 ? "+" : ""}${v}°C` },
];

const FOUNDING_MEMBERS = [
  {
    name: "Bordeaux School of Economics",
    country: "France",
    logo: "/founding-members/bordeaux-school-of-economics.png",
    url: "https://www.bse.u-bordeaux.fr/",
  },
  {
  name: "Bordeaux Sciences Agro",
  country: "France",
  logo: "/founding-members/bordeaux-sciences-agro.png",
  url: "https://www.agro-bordeaux.fr/",
},
  {
    name: "Consejo Superior de Investigaciones Científicas",
    country: "Spain",
    logo: "/founding-members/csic.png",
    url: "https://www.csic.es/",
    darkLogo: true,
  },
  {
    name: "Eastern Connecticut State University",
    country: "United States",
    logo: "/founding-members/eastern-connecticut-state-university.png",
    url: "https://www.easternct.edu/index.html",
  },
  {
    name: "HES-SO / HEG-Genève",
    country: "Switzerland",
    logo: "/founding-members/heg-geneve.png",
    url: "https://heg.hesge.ch/",
  },
  {
    name: "Hochschule Geisenheim University",
    country: "Germany",
    logo: "/founding-members/hochschule-geisenheim-university.png",
    url: "https://www.hs-geisenheim.de/",
  },
  {
    name: "INSEEC Grande Ecole",
    country: "France",
    logo: "/founding-members/inseec-grande-ecole.png",
    url: "https://www.inseec.com/",
  },
  {
    name: "KEDGE Business School",
    country: "France",
    logo: "/founding-members/kedge-business-school.png",
    url: "https://kedge.edu/",
  },
  {
  name: "Universidad de Alicante",
  country: "Spain",
  logo: "/founding-members/universidad-de-alicante.png",
  url: "https://www.ua.es/",
  },
  {
  name: "Universidad Técnica Federico Santa María",
  country: "Chile",
  logo: "/founding-members/universidad-tecnica-federico-santa-maria.svg",
  url: "https://usm.cl/",
  darkLogo: true,
},
  {
  name: "Università Mercatorum",
  country: "Italy",
  logo: "/founding-members/universita-mercatorum.png",
  url: "https://www.unimercatorum.it/",
  darkLogo: true,
},
  {
    name: "Università Telematica Pegaso",
    country: "Italy",
    logo: "/founding-members/universita-telematica-pegaso.png",
    url: "https://www.unipegaso.it/",
  },
  {
    name: "University of Melbourne",
    country: "Australia",
    logo: "/founding-members/university-of-melbourne.png",
    url: "https://www.unimelb.edu.au/",
  },
  {
    name: "University of North Texas",
    country: "United States",
    logo: "/founding-members/university-of-north-texas.png",
    url: "https://www.unt.edu/index.html",
  },
  {
    name: 'University of Torino, Department of Economics and Statistics "Cognetti de Martiis"',
    country: "Italy",
    logo: "/founding-members/university-of-torino-cognetti-de-martiis.png",
    url: "https://www.est-en.unito.it/do/home.pl",
  },
  {
    name: "University of Western Australia, Centre for Agricultural and Viticultural Economics",
    country: "Australia",
    logo: "/founding-members/uwa-cave.png",
    url: "https://www.uwacave.org/",
  },
  {
    name: "University of Zagreb",
    country: "Croatia",
    logo: "/founding-members/university-of-zagreb.png",
    url: "https://www.unizg.hr/homepage/",
    darkLogo: true,
  },
];

function CountryTeaser({ t }) {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState("France");
  useEffect(() => {
    fetch("/data/country_scorecard.json").then((r) => r.json()).then(setData).catch(() => {});
  }, []);
  const c = data?.countries?.[sel];
  const total = data?.peers?.length || 21;
  return (
    <Flex direction="column" h="100%" bg="white" borderWidth="1px" borderColor="border.default" borderRadius="ui-lg" boxShadow="ui-sm" p={{ base: 5, md: 6 }}>
      <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.14em" textTransform="uppercase" color={ACCENT} mb={3}>
        {t("landing.country_portrait", "Country portrait")}
      </Text>
      <Flex gap={2} flexWrap="wrap" mb={5}>
        {PORTRAIT_COUNTRIES.map((name) => {
          const active = name === sel;
          return (
            <Button
              key={name}
              size="sm"
              borderRadius="full"
              bg={active ? ACCENT : "transparent"}
              color={active ? "white" : "gray.700"}
              borderWidth="1px"
              borderColor={active ? ACCENT : "border.default"}
              _hover={{ bg: active ? ACCENT_HOVER : "surface.subtle" }}
              onClick={() => setSel(name)}
            >
              {name}
            </Button>
          );
        })}
      </Flex>
      <Heading fontFamily={SERIF} fontSize="2xl" color="gray.900" mb={4}>{sel}</Heading>
      <SimpleGrid columns={2} spacingX={6} spacingY={4} mb={5}>
        {PORTRAIT_METRICS.map((m) => (
          <Box key={m.key}>
            <Text fontFamily={SERIF} fontSize="3xl" fontWeight="bold" color={ACCENT} lineHeight="1">
              {c && c[m.key] != null ? m.fmt(c[m.key]) : "—"}
            </Text>
            <Text fontSize="xs" color="gray.600" textTransform="uppercase" letterSpacing="0.06em" mt={1}>
              {m.label}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
      <Box mt="auto" borderTopWidth="1px" borderColor="border.default" pt={4}>
        <Text fontSize="sm" color="gray.600" mb={3}>
          {t("landing.gate_text", "Previewing {{n}} of {{total}} countries — create a free account to unlock every country portrait, head-to-head comparisons and the full research tools.", { n: PORTRAIT_COUNTRIES.length, total })}
        </Text>
        <Button as={RouterLink} to="/register" bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }} size="md" w={{ base: "100%", sm: "auto" }}>
          {t("landing.gate_cta", "Create a free account")} →
        </Button>
      </Box>
    </Flex>
  );
}

function Eyebrow({ children }) {
  return <Text color={ACCENT} fontSize="sm" fontWeight="semibold" letterSpacing="0.18em" textTransform="uppercase" mb={3}>{children}</Text>;
}

function CommunityCard({ tag, title, icon, children, footer }) {
  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="ui-lg" bg="white"
      boxShadow="ui-sm" p={6} display="flex" flexDirection="column" minH={{ base: "auto", md: "400px" }} maxH={{ md: "440px" }}>
      <HStack mb={4} align="flex-start" spacing={3}>
        <Flex boxSize="38px" borderRadius="md" bg="surface.subtle" align="center" justify="center" flexShrink={0}>
          <Icon as={icon} color={ACCENT} boxSize={5} />
        </Flex>
        <Box>
          <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.12em" textTransform="uppercase" color={ACCENT}>{tag}</Text>
          <Heading as="h3" fontFamily={SERIF} fontSize="lg" color="gray.900" lineHeight="1.2">{title}</Heading>
        </Box>
      </HStack>
      <Box flex="1" minH={0} overflowY="auto" pr={1}>{children}</Box>
      {footer && <Box pt={3}>{footer}</Box>}
    </Box>
  );
}

function LangMenu() {
  const { i18n } = useTranslation();
  return (
    <Menu>
      <MenuButton as={Button} variant="ghost" size="sm" leftIcon={<FiGlobe />} rightIcon={<FiChevronDown />}>
        {(i18n.language || "en").slice(0, 2).toUpperCase()}
      </MenuButton>
      <MenuList minW="160px">
        {LANGUAGE_OPTIONS.map(({ code, label }) => (
          <MenuItem key={code} onClick={() => i18n.changeLanguage(code)}
            fontWeight={(i18n.language || "en").startsWith(code) ? "bold" : "normal"}
            color={(i18n.language || "en").startsWith(code) ? ACCENT : undefined}>
            {label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}

function ForgotPasswordModal({ isOpen, onClose, initialEmail }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(initialEmail || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (isOpen) { setEmail(initialEmail || ""); setMsg(""); } }, [isOpen, initialEmail]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setBusy(true);
    const val = email.trim();
    if (!val) { setMsg(t("login_text.forgot_email_required", "Please enter your email.")); setBusy(false); return; }
    const { ok, detail } = await requestPasswordReset(val);
    setMsg(ok
      ? t("login_text.forgot_success", "If that email exists, a reset link is on its way.")
      : (detail
          ? t("login_text.forgot_error_with_detail", { detail, defaultValue: `Could not send reset: ${detail}` })
          : t("login_text.forgot_error_default", "Could not send a reset email. Please try again.")));
    setBusy(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={submit}>
        <ModalHeader fontFamily={SERIF}>{t("forgot_password", "Forgot password?")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" color="text.muted" mb={3}>
            {t("login_text.forgot_prompt", "Enter your account email and we'll send you a reset link.")}
          </Text>
          <Input type="email" placeholder={t("email", "Email")} value={email}
            onChange={(e) => setEmail(e.target.value)} autoFocus />
          {msg && <Text fontSize="sm" mt={3} color="gray.700">{msg}</Text>}
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>{t("cancel", "Cancel")}</Button>
          <Button type="submit" isLoading={busy} bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }}>
            {t("login_text.send_reset", "Send reset link")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function LoginCard({ setIsLoggedIn }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const forgot = useDisclosure();
  const [email, setEmail] = useState(
    () => localStorage.getItem("rememberedEmail") || localStorage.getItem("userEmail") || ""
  );
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [rememberMe, setRememberMe] = useState(
    () => localStorage.getItem("rememberMe") !== "false"
  );
  const [err, setErr] = useState("");
  const [pendingEmail, setPendingEmail] = useState(""); // email awaiting verification
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);

  const errorFor = (res) => {
    switch (res.reason) {
      case "validation_email": return t("login_text.validation_email_required", "Please enter your email.");
      case "validation_password": return t("login_text.validation_password_invalid", "Password must be at least 6 characters.");
      case "unverified": return t("login_text.verify_required_message", "Please verify your email before signing in.");
      case "failed": return res.detail
        ? t("login_text.login_failed_with_detail", { detail: res.detail, defaultValue: `Sign-in failed: ${res.detail}` })
        : t("login_text.login_failed_default", "Sign-in failed. Please check your details.");
      default: return t("login_text.login_generic_error", "Something went wrong. Please try again.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setPendingEmail(""); setBusy(true);
    const res = await performLogin({ email, password: pwd, rememberMe });
    if (res.ok) {
      if (setIsLoggedIn) setIsLoggedIn(true);
      try { const { trackEvent } = await import("./services/trackingService"); trackEvent("login"); } catch (_) {}
      navigate("/dashboard");
    } else {
      if (res.reason === "unverified") setPendingEmail(res.email || email.trim());
      setErr(errorFor(res));
      setBusy(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    const { ok } = await resendVerification(pendingEmail);
    setResending(false);
    setErr(ok
      ? t("login_text.verification_email_sent_desc", "Verification email sent — check your inbox.")
      : t("login_text.verification_email_error_desc", "Could not send the verification email."));
    if (ok) setPendingEmail("");
  };

  return (
    <>
      <Box as="form" onSubmit={submit} bg="white" borderRadius="ui-lg" boxShadow="ui-md" p={6} w="100%" maxW="380px">
        <Heading as="h2" fontFamily={SERIF} fontSize="2xl" mb={1}>{t("landing.sign_in", "Sign in")}</Heading>
        <Text fontSize="sm" color="text.muted" mb={4}>{t("landing.welcome_back", "Welcome back to the institute.")}</Text>

        {err && (
          <Alert status={pendingEmail ? "warning" : "error"} borderRadius="md" mb={3} alignItems="flex-start" flexDirection="column" gap={2} pr={8} position="relative">
            <CloseButton position="absolute" top="6px" right="6px" size="sm" onClick={() => { setErr(""); setPendingEmail(""); }} />
            <HStack align="flex-start"><AlertIcon mt="2px" /><Text fontSize="sm">{err}</Text></HStack>
            {pendingEmail && (
              <Button size="xs" onClick={handleResend} isLoading={resending} bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }}>
                {t("login_text.resend_verification_button", "Resend verification email")}
              </Button>
            )}
          </Alert>
        )}

        <VStack align="stretch" spacing={3}>
          <Input type="email" placeholder={t("email", "Email")} value={email} autoComplete="username"
            onChange={(e) => setEmail(e.target.value)} required />
          <InputGroup>
            <Input type={showPwd ? "text" : "password"} placeholder={t("password", "Password")} value={pwd}
              autoComplete="current-password" onChange={(e) => setPwd(e.target.value)} required pr="2.5rem" />
            <InputRightElement>
              <IconButton variant="ghost" size="sm" tabIndex={-1}
                aria-label={showPwd ? "Hide password" : "Show password"}
                icon={showPwd ? <FiEyeOff /> : <FiEye />} onClick={() => setShowPwd((v) => !v)} />
            </InputRightElement>
          </InputGroup>
          <Flex justify="space-between" align="center">
            <Checkbox colorScheme="gwbi" isChecked={rememberMe}
              onChange={(e) => { setRememberMe(e.target.checked); localStorage.setItem("rememberMe", e.target.checked ? "true" : "false"); }}
              fontSize="sm">
              {t("remember_me", "Remember me")}
            </Checkbox>
            <Link as="button" type="button" color={ACCENT} fontSize="sm" fontWeight="semibold"
              onClick={(e) => { e.preventDefault(); forgot.onOpen(); }}>
              {t("forgot_password", "Forgot password?")}
            </Link>
          </Flex>
          <Button type="submit" isLoading={busy} bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }}>{t("login", "Enter")}</Button>
        </VStack>

        <Text fontSize="sm" color="text.muted" mt={4} textAlign="center">
          {t("landing.no_account", "New here?")}{" "}
          <Link as={RouterLink} to="/register" color={ACCENT} fontWeight="semibold">{t("create_account_cta", "Create free account")}</Link>
        </Text>
      </Box>

      <ForgotPasswordModal isOpen={forgot.isOpen} onClose={forgot.onClose} initialEmail={email} />
    </>
  );
}

export default function LandingPage({ setIsLoggedIn }) {
  const { t, i18n } = useTranslation();
  const [videoIdx] = useState(() => Math.floor(Math.random() * VIDEOS.length));
  const [weekly, setWeekly] = useState(null);
  const loginModal = useDisclosure();
  const storyDeck = useDisclosure();

  // #story opens the deck straight away, so the tour can be linked to on its
  // own — in a newsletter, a talk, or an email to someone who asked what we do.
  const { onOpen: openStory } = storyDeck;
  useEffect(() => {
    if (window.location.hash === "#story") openStory();
  }, [openStory]);
  const [pubs, setPubs] = useState([]);
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);

  // Open the sign-in modal automatically when arriving from email verification (/?login=1)
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("login") === "1") {
      loginModal.onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const lang = (i18n.language || "en").slice(0, 2);
    const get = (u) => fetch(u, { headers: DEFAULT_HEADERS }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    get(`${API_BASE}/api/weekly-summary/latest?lang=${lang}`).then((d) => d?.year && setWeekly(d));
    // Ask for what we show. These used to fetch every row -- 3,195 news
    // items, 3.2MB -- and slice off the first few. The slices stay as a
    // safety net for a server that has not learned ?limit yet.
    get(`${API_BASE}/wineries/publications?limit=3`).then((d) => Array.isArray(d) && setPubs(d.slice(0, 3)));
    get(`${API_BASE}/wineries/news?limit=4`).then((d) => Array.isArray(d) && setNews(d.slice(0, 4)));
    get(`${API_BASE}/wineries/conferences`).then((d) => Array.isArray(d) && setEvents(d));
  }, [i18n.language]);

  const upcoming = [...events]
    .filter((e) => { const d = new Date(e?.date_conference || e?.date); return !isNaN(d) && d >= new Date(); })
    .sort((a, b) => new Date(a?.date_conference || a?.date) - new Date(b?.date_conference || b?.date))
    .slice(0, 4);

  return (
    <Box bg={PAPER} minH="100vh">
      {/* nav */}
      <Flex as="header" position="sticky" top={0} zIndex={20} bg="rgba(251,250,248,0.92)"
        backdropFilter="blur(6px)" borderBottomWidth="1px" borderColor="border.default"
        px={{ base: 5, md: 10 }} py={3} align="center" justify="space-between">
        <HStack spacing={3}>
          <Image src="/gwbi_logo_red.png" alt="" h="60px" fallbackSrc="data:," />
          <Text fontFamily={SERIF} fontWeight="bold" fontSize={{ base: "lg", md: "xl" }} letterSpacing="-0.01em" color={ACCENT} display={{ base: "none", sm: "block" }}>Global Wine Business Institute</Text>
        </HStack>
        <HStack spacing={{ base: 1, md: 3 }}>
          <LangMenu />
          <Button onClick={loginModal.onOpen} variant="outline" size="sm" borderColor="border.default" color="gray.700" _hover={{ bg: "surface.subtle" }}>{t("landing.sign_in", "Log in")}</Button>
          <Button as={RouterLink} to="/register" size="sm" bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }}>{t("landing.join", "Join")}</Button>
        </HStack>
      </Flex>

      {/* video hero with big logo + inline login */}
      <Box position="relative" overflow="hidden">
        <Box as="video" key={videoIdx} autoPlay muted playsInline loop poster={POSTERS[videoIdx]} position="absolute" inset={0} w="100%" h="100%" objectFit="cover" zIndex={0}>
          <source src={VIDEOS[videoIdx]} type="video/mp4" />
        </Box>
        <Box position="absolute" inset={0} zIndex={1}
          bgGradient="linear(to-r, rgba(35,18,14,0.6), rgba(35,18,14,0.3) 55%, rgba(35,18,14,0.12))" />
        <Container maxW="6xl" position="relative" zIndex={2} py={{ base: 14, md: 24 }}>
          <Flex gap={{ base: 10, md: 16 }} align="center" direction={{ base: "column", md: "row" }}>
            <Box flex="1.2" color="white">
              <Image src="/gwbi_logo_red.png" alt="GWBI" display="block" h={{ base: "126px", md: "180px" }} mb={6}
                sx={{ filter: "brightness(0) invert(1)" }} fallbackSrc="data:," />
              <Heading as="h1" fontFamily={SERIF} fontWeight="bold" fontSize={{ base: "4xl", md: "6xl" }} lineHeight="1.04" mb={4}
                sx={{ textShadow: "0 2px 20px rgba(0,0,0,0.35)" }}>
                {t("app_title", "A Global Network for Wine Business & Economics")}
              </Heading>
              <Text fontSize={{ base: "lg", md: "xl" }} color="whiteAlpha.900" maxW="xl" mb={6}>
                {t("reg_tagline", "Where Wine Meets Knowledge — Connect, Share, and Grow in the Global Wine Business.")}
              </Text>
              <HStack spacing={3} flexWrap="wrap">
                <Button as={RouterLink} to="/atlas" size="lg" bg={ACCENT} color="white" _hover={{ bg: ACCENT_HOVER }}>
                  {t("landing.explore_observatory", "Explore the Observatory")} →
                </Button>
                <Button size="lg" variant="outline" color="white" borderColor="whiteAlpha.700"
                  leftIcon={<Icon as={FiPlayCircle} />}
                  _hover={{ bg: "whiteAlpha.200", borderColor: "white" }}
                  onClick={storyDeck.onOpen}>
                  {t("landing.watch_story", "What is the GWBI?")}
                </Button>
              </HStack>
            </Box>
          </Flex>
        </Container>
      </Box>

      {/* explore the data — world map + gated country portrait */}
      <Box bg="surface.canvas" borderBottomWidth="1px" borderColor="border.default">
        <Container maxW="6xl" py={{ base: 12, md: 16 }}>
          <Eyebrow>{t("landing.explore_data", "Explore the data")}</Eyebrow>
          <Heading fontFamily={SERIF} fontSize={{ base: "2xl", md: "3xl" }} mb={2} maxW="3xl">
            {t("landing.data_heading", "The first global, firm-level picture of how the world makes wine.")}
          </Heading>
          <Text color="gray.600" maxW="2xl" mb={8}>
            {t("landing.data_sub", "21 wine-producing countries, surveyed winery by winery — exports, organic share, premiumisation and climate. Preview a few below, or open the interactive atlas.")}
          </Text>
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, md: 10 }} alignItems="stretch">
            <Flex direction="column" h="100%" bg="white" borderWidth="1px" borderColor="border.default" borderRadius="ui-lg" boxShadow="ui-sm" p={{ base: 5, md: 6 }}>
              <Box borderRadius="ui-md" overflow="hidden" borderWidth="1px" borderColor="border.default">
                <Image src="/world_map_teaser.png" alt={t("landing.map_alt", "The 21 wine-survey countries")} w="100%" display="block" />
              </Box>
              <Box mt="auto" pt={5}>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  {t("landing.map_text", "All 21 wine-producing countries on one interactive map — shade by exports, organic share, premiumisation or climate and drill into any country.")}
                </Text>
                <Button as={RouterLink} to="/atlas" variant="outline" borderColor="gwbi.300" color="gwbi.700" _hover={{ bg: "gwbi.50" }} size="md" w={{ base: "100%", sm: "auto" }}>
                  {t("landing.open_atlas", "Open the interactive atlas")} →
                </Button>
              </Box>
            </Flex>
            <CountryTeaser t={t} />
          </SimpleGrid>
        </Container>
      </Box>

      {/* inside the network — feature grid */}
      <Box bg="white" borderBottomWidth="1px" borderColor="border.default">
        <Container maxW="6xl" py={{ base: 12, md: 16 }}>
          <Eyebrow>{t("landing.inside_network", "Inside the network")}</Eyebrow>
          <Heading fontFamily={SERIF} fontSize={{ base: "2xl", md: "3xl" }} mb={10} maxW="3xl">
            {t("landing.everything", "Everything the institute brings together.")}
          </Heading>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacingX={10} spacingY={7}>
            {FEATURE_KEYS.map(([icon, key]) => (
              <HStack key={key} align="flex-start" spacing={3}>
                <Icon as={icon} color={ACCENT} boxSize={5} mt={1} flexShrink={0} />
                <Box>
                  <Text fontWeight="bold" color="gray.900">{t(`login_slides.${key}.title`)}</Text>
                  <Text fontSize="sm" color="gray.600">{t(`login_slides.${key}.subtitle`)}</Text>
                </Box>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* live from the community */}
      <Container maxW="7xl" py={{ base: 12, md: 16 }}>
        <Eyebrow>{t("landing.live_community", "Live from the community")}</Eyebrow>
        <Heading fontFamily={SERIF} fontSize={{ base: "2xl", md: "3xl" }} mb={8}>{t("weekly_summary.title", "This Week in Wine")}</Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
          <CommunityCard tag={weekly ? t("weekly_summary.week_label", "Week {{week}}, {{year}}", { week: weekly.week, year: weekly.year }) : t("weekly_summary.title", "This week")} title={t("weekly_summary.title", "This Week in Wine")} icon={FiCoffee}>
            {weekly ? (
              <VStack align="stretch" spacing={4}>
                {[[t("weekly_summary.journal_tab", "Business"), weekly.journal_news],
                  [t("weekly_summary.academic_tab", "Academic"), weekly.academic_news],
                  [t("weekly_summary.policy_tab", "Policy"), weekly.policy_news]]
                  .filter(([, v]) => v).map(([h, v]) => (
                    <Box key={h}>
                      <Text fontSize="xs" fontWeight="bold" color={ACCENT} textTransform="uppercase" letterSpacing="0.08em" mb={1}>{h}</Text>
                      <Text fontSize="sm" color="gray.700" noOfLines={4}>{stripMd(v)}</Text>
                    </Box>
                  ))}
              </VStack>
            ) : <Text fontSize="sm" color="text.muted">…</Text>}
          </CommunityCard>

          <CommunityCard tag={t("news", "News")} title={t("landing.newswire", "Wine newswire")} icon={FiFileText}
            footer={<Link as={RouterLink} to="/register" color={ACCENT} fontSize="sm" fontWeight="semibold">{t("landing.more_platform", "More on the platform")} →</Link>}>
            <VStack align="stretch" spacing={4}>
              {news.length ? news.map((n, i) => (
                <HStack key={i} align="flex-start" spacing={3}>
                  <Image src={n.image || "/wineglass.png"} fallbackSrc="/wineglass.png" boxSize="56px" objectFit="cover" borderRadius="md" flexShrink={0} />
                  <Box>
                    <Link href={n.link} isExternal fontSize="sm" fontWeight="600" color="gray.900" noOfLines={2}>{getLocalizedField(n, "title", i18n.language)}</Link>
                    <Text fontSize="xs" color="text.muted">{[(n.source || "News").replace(/_/g, " "), fmtDate(n.date_published)].filter(Boolean).join(" · ")}</Text>
                  </Box>
                </HStack>
              )) : <Text fontSize="sm" color="text.muted">…</Text>}
            </VStack>
          </CommunityCard>

          <CommunityCard tag={t("research", "Research")} title={t("landing.latest_journals", "Latest from the journals")} icon={FiBookOpen}>
            <VStack align="stretch" spacing={4}>
              {pubs.length ? pubs.map((p, i) => (
                <HStack key={i} align="flex-start" spacing={3}>
                  <Image src={getJournalLogoPath(p.journal)} fallbackSrc="/journal_generic.svg" h="30px" maxW="76px" objectFit="contain" flexShrink={0} mt={1} />
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color={ACCENT} noOfLines={1}>{p.journal}</Text>
                    <Text fontSize="sm" fontWeight="600" color="gray.900" noOfLines={2}>{getLocalizedField(p, "title", i18n.language)}</Text>
                    <Text fontFamily={SERIF} fontStyle="italic" fontSize="xs" color="gray.600" noOfLines={2} mt={0.5}>{getLocalizedField(p, "insights", i18n.language)}</Text>
                    <Text fontSize="xs" color="text.muted" mt={1}>{[p.author, fmtDate(p.date_published)].filter(Boolean).join(" · ")}</Text>
                  </Box>
                </HStack>
              )) : <Text fontSize="sm" color="text.muted">…</Text>}
            </VStack>
          </CommunityCard>

          <CommunityCard tag={t("conferences", "Events")} title={t("landing.upcoming", "Upcoming")} icon={FiCalendar}
            footer={<Link as={RouterLink} to="/register" color={ACCENT} fontSize="sm" fontWeight="semibold">{t("landing.all_events", "All events")} →</Link>}>
            <VStack align="stretch" spacing={4}>
              {upcoming.length ? upcoming.map((e, i) => {
                const d = new Date(e?.date_conference || e?.date);
                const date = isNaN(d) ? "" : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
                return (
                  <HStack key={i} align="flex-start" spacing={3}>
                    <Flex boxSize="44px" bg={ACCENT} color="white" borderRadius="md" align="center" justify="center" flexShrink={0}>
                      <Icon as={FiCalendar} boxSize={5} />
                    </Flex>
                    <Box>
                      <Text fontSize="sm" fontWeight="600" color="gray.900" noOfLines={2}>{e.name || e.title}</Text>
                      <Text fontSize="xs" color="text.muted">{[date, e.location, e.focus].filter(Boolean).join(" · ")}</Text>
                    </Box>
                  </HStack>
                );
              }) : <Text fontSize="sm" color="text.muted">…</Text>}
            </VStack>
          </CommunityCard>
        </SimpleGrid>
      </Container>

      {/* visualising wine */}
      <Box bg="white" borderTopWidth="1px" borderColor="border.default">
        <Container maxW="6xl" py={{ base: 12, md: 16 }}>
          <Flex justify="space-between" align="flex-end" mb={8} wrap="wrap" gap={2}>
            <Box>
              <Eyebrow>{t("landing.vw_eyebrow", "Visualising Wine — for policymakers, industry & academics")}</Eyebrow>
              <Heading fontFamily={SERIF} fontSize={{ base: "2xl", md: "3xl" }} maxW="2xl">
                {t("landing.vw_heading", "Explore the data, read the climate stories, compare any country.")}
              </Heading>
              <Text color="gray.600" mt={2} maxW="2xl">
                {t("landing.vw_sub", "An evidence base built for policymakers, companies and academics — descriptive, sourced and citable.")}
              </Text>
            </Box>
            <Button as={RouterLink} to="/visualising-wine" variant="link" color={ACCENT} fontWeight="semibold">{t("landing.see_all", "See all")} →</Button>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[["landing.vw_card1_tag", "Explore", "landing.vw_card1_title", "The interactive Atlas", "landing.vw_card1_desc", "Every winery on one map — by climate, variety, exports, terroir and more.", "/atlas"],
              ["landing.vw_card2_tag", "Story", "landing.vw_card2_title", "Three decades of warming", "landing.vw_card2_desc", "Scroll and watch 5,106 vineyards warm between the 1980s and today.", "/story/warming"],
              ["landing.vw_card3_tag", "Tool", "landing.vw_card3_title", "Country Portrait", "landing.vw_card3_desc", "Pick a country and compare it vs. peers, head to head.", "/scorecard"]].map(([tgK, tgD, tiK, tiD, hkK, hkD, to]) => (
              <LinkBox key={tiK} borderWidth="1px" borderColor="border.default" borderRadius="ui-lg" bg={PAPER}
                boxShadow="ui-sm" p={6} transition="all .2s" _hover={{ boxShadow: "ui-md", transform: "translateY(-3px)" }}>
                <Text fontSize="xs" fontWeight="semibold" letterSpacing="0.14em" textTransform="uppercase" color={ACCENT} mb={1}>{t(tgK, tgD)}</Text>
                <Heading as="h3" fontFamily={SERIF} fontSize="xl" mb={2}><LinkOverlay as={RouterLink} to={to}>{t(tiK, tiD)}</LinkOverlay></Heading>
                <Text color="gray.600" fontSize="sm">{t(hkK, hkD)}</Text>
              </LinkBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* founding members */}
      <Box
        bg="surface.canvas"
        borderTopWidth="1px"
        borderColor="border.default"
      >
        <Container maxW="6xl" py={{ base: 12, md: 16 }}>
          <Eyebrow>{t("landing.founding_members")}</Eyebrow>

          <Heading
            fontFamily={SERIF}
            fontSize={{ base: "2xl", md: "3xl" }}
            mb={2}
            maxW="3xl"
          >
            {t("landing.founding_members_heading")}
          </Heading>

          <Text color="gray.600" maxW="2xl" mb={10}>
            {t("landing.founding_members_desc")}
          </Text>

          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing={5}
          >
            {FOUNDING_MEMBERS.map((member) => (
              <LinkBox
                key={member.name}
                bg="white"
                borderWidth="1px"
                borderColor="border.default"
                borderRadius="ui-lg"
                boxShadow="ui-sm"
                overflow="hidden"
                transition="all .2s"
                _hover={{
                  boxShadow: "ui-md",
                  transform: "translateY(-3px)",
                }}
              >
                <Flex
                  h="150px"
                  align="center"
                  justify="center"
                  p={6}
                  bg={member.darkLogo ? "#1f2d56" : "white"}
                  borderBottomWidth="1px"
                  borderColor="border.default"
                >
                  <Image
                    src={member.logo}
                    alt={`${member.name} logo`}
                    maxH="95px"
                    maxW="100%"
                    objectFit="contain"
                  />
                </Flex>

                <Box p={5}>
                  <Heading
                    as="h3"
                    fontFamily={SERIF}
                    fontSize="md"
                    lineHeight="1.3"
                    mb={2}
                    color="gray.900"
                  >
                    <LinkOverlay
                      href={member.url}
                      isExternal
                    >
                      {member.name}
                    </LinkOverlay>
                  </Heading>

                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color={ACCENT}
                    textTransform="uppercase"
                    letterSpacing="0.08em"
                  >
                    {t(`countries.${member.country.toLowerCase().replace(/\s+/g, "_")}`, member.country)}
                  </Text>
                </Box>
              </LinkBox>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* join */}
      <Box bg={ACCENT} color="white">
        <Container maxW="4xl" py={{ base: 12, md: 16 }} textAlign="center">
          <Heading fontFamily={SERIF} fontSize={{ base: "2xl", md: "4xl" }} mb={4}>{t("landing.join_institute", "Join the institute")}</Heading>
          <Text fontSize={{ base: "md", md: "lg" }} color="whiteAlpha.900" maxW="2xl" mx="auto" mb={7}>
            {t("landing.join_blurb", "Connect with researchers, companies and policymakers worldwide — bring a question, run the research tools, and join seminars across the global wine community. We maintain the survey; members simply answer it.")}
          </Text>
          <Button as={RouterLink} to="/register" size="lg" bg="white" color={ACCENT} _hover={{ bg: "gray.100" }}>{t("landing.create_free", "Create a free account")}</Button>
        </Container>
      </Box>

      <Box as="footer" py={8} px={{ base: 5, md: 10 }} borderTopWidth="1px" borderColor="border.default">
        <Container maxW="6xl">
          <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
            <Text fontSize="xs" color="text.muted">© Global Wine Business Institute</Text>
            <HStack spacing={5} fontSize="xs">
              <Link as={RouterLink} to="/privacy" color="text.muted" _hover={{ color: ACCENT }}>Privacy</Link>
              <Link as={RouterLink} to="/terms" color="text.muted" _hover={{ color: ACCENT }}>Terms</Link>
              <Link as={RouterLink} to="/contact" color="text.muted" _hover={{ color: ACCENT }}>Contact</Link>
              <HStack spacing={3}>
                {SOCIAL_LINKS.map((social) => (
                  <Link
                    key={social.name}
                    href={social.url}
                    isExternal
                    aria-label={`GWBI on ${social.name}`}
                    title={social.name}
                    color="text.muted"
                    _hover={{ color: ACCENT }}
                  >
                    <Icon as={social.icon} boxSize="16px" />
                  </Link>
                ))}
              </HStack>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Modal isOpen={loginModal.isOpen} onClose={loginModal.onClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent bg="transparent" boxShadow="none" m={4}>
          <LoginCard setIsLoggedIn={setIsLoggedIn} />
        </ModalContent>
      </Modal>

      <StoryDeck isOpen={storyDeck.isOpen} onClose={storyDeck.onClose} />
    </Box>
  );
}
